import React, { useEffect, useRef, useState } from 'react';

const Carousel = ({
  children,
  itemsPerView = 1,
  itemsPerViewMobile = 1, // Items to show on mobile
  gap = 4,
  speed = 20, // seconds per full cycle
  direction = 'left', // 'left' or 'right'
  className = ''
}) => {
  const scrollContainerRef = useRef(null);
  const animationRef = useRef(null);
  const childrenArray = React.Children.toArray(children);
  const [isMobile, setIsMobile] = useState(false);
  const [currentItemsPerView, setCurrentItemsPerView] = useState(itemsPerView);

  // Detectar si es mobile (menor a 768px, breakpoint md de Tailwind)
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setCurrentItemsPerView(mobile ? itemsPerViewMobile : itemsPerView);
    };

    // Verificar al montar
    checkMobile();

    // Escuchar cambios de tamaño
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [itemsPerView, itemsPerViewMobile]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const itemWidth = container.offsetWidth / currentItemsPerView;
    const totalWidth = itemWidth * childrenArray.length;
    const pixelsPerSecond = totalWidth / speed;
    const isMovingLeft = direction === 'left';

    let position = isMovingLeft ? 0 : totalWidth;

    const animate = () => {
      if (isMovingLeft) {
        position += pixelsPerSecond / 60; // 60 FPS

        // Reset position when we've scrolled through all original items
        if (position >= totalWidth) {
          position = 0;
        }

        container.style.transform = `translateX(-${position}px)`;
      } else {
        position -= pixelsPerSecond / 60; // 60 FPS

        // Reset position when we've scrolled back to the start
        if (position <= 0) {
          position = totalWidth;
        }

        container.style.transform = `translateX(-${position}px)`;
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [childrenArray.length, currentItemsPerView, speed, direction]);

  // Duplicate items to create seamless loop
  const duplicatedChildren = [...childrenArray, ...childrenArray];

  return (
    <div className={`relative overflow-hidden rounded-2xl py-8 ${className}`}>
      <div
        ref={scrollContainerRef}
        className="flex"
        style={{ willChange: 'transform' }}
      >
        {duplicatedChildren.map((child, index) => (
          <div
            key={index}
            className="flex-shrink-0"
            style={{
              width: `calc(${100 / currentItemsPerView}% - ${gap * (currentItemsPerView - 1) / currentItemsPerView}rem)`,
              marginRight: `${gap}rem`
            }}
          >
            {child}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Carousel;